import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Pulse, Sparkle, FileText, Truck, CheckCircle, Clock,
  WarningOctagon, Receipt, Users as UsersIcon, ListChecks,
  CaretRight, ChartBar, ArrowRight,
} from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pos, setPos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/po").then((r) => setPos(r.data));
    api.get("/invoices").then((r) => setInvoices(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);

  const openOrders = pos.filter((p) =>
    ["pending_review", "confirmed", "partially_shipped"].includes(p.status)
  );
  const pendingReview = pos.filter((p) => p.status === "pending_review");
  const partial = pos.filter((p) => p.status === "partially_shipped");
  const fullyShipped = pos.filter((p) => p.status === "fully_shipped");
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const aging30 = stats?.aging_over_30 ?? 0;
  const discrep = stats?.discrepancies ?? 0;

  return (
    <Layout breadcrumb={["Operations Control Room"]}>
      {/* ===== HERO ===== */}
      <div className="omh-hero" data-testid="hero-banner">
        <div className="omh-hero-content">
          <div className="omh-hero-eyebrow">
            <Pulse size={11} weight="bold" style={{ display: "inline", marginRight: 6, marginBottom: -1 }} />
            OPERATIONS CONTROL ROOM · OHE · {greeting}
          </div>
          <h1 className="omh-hero-title">Welcome back, {capitalize(user?.username || "")}</h1>
          <div className="omh-advisor" data-testid="ai-advisor">
            <span className="omh-advisor-label"><Sparkle size={12} weight="fill" /> AI ADVISOR</span>
            <span>
              {discrep > 0
                ? `${discrep} shipment discrepancy alert${discrep > 1 ? "s" : ""} — review now`
                : aging30 > 0
                  ? `${aging30} open PO${aging30 > 1 ? "s" : ""} aging over 30 days — follow up with suppliers`
                  : openOrders.length > 0
                    ? `${openOrders.length} open order${openOrders.length > 1 ? "s" : ""} in pipeline — all healthy`
                    : "All clear — no urgent items at this time"}
            </span>
          </div>
        </div>

        <div className="omh-mini-grid">
          <MiniTile label="Open POs" value={stats?.open_orders ?? 0} onClick={() => nav("/orders")} testid="mini-open" />
          <MiniTile label="Pending Review" value={stats?.pending_review ?? 0} variant="warning" onClick={() => nav("/orders?status=pending_review")} testid="mini-pending" />
          <MiniTile label="Aging > 30d" value={aging30} variant={aging30 > 0 ? "alert" : ""} onClick={() => nav("/aging")} testid="mini-aging" />
          <MiniTile label="Discrepancies" value={discrep} variant={discrep > 0 ? "alert" : ""} onClick={() => nav("/discrepancies")} testid="mini-discrep" />
        </div>
      </div>

      {/* ===== KPI TILES ===== */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginTop: 18 }}>
        <Tile label="Open Orders" value={stats?.open_orders ?? "—"} icon={FileText} variant="info" sub="active pipeline" testid="tile-open" />
        <Tile label="Partially Shipped" value={stats?.partially_shipped ?? "—"} icon={Truck} variant="info" sub="in transit" testid="tile-partial" />
        <Tile label="Fully Shipped" value={stats?.fully_shipped ?? "—"} icon={CheckCircle} variant="success" sub="last 90d" testid="tile-shipped" />
        <Tile label="Invoices Captured" value={invoices.length} icon={Receipt} variant="warning" sub="from SAP emails" testid="tile-invoices" />
      </div>

      {/* ===== TWO COLUMN: Your Queue + Watchlists ===== */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 24, marginTop: 8 }}>
        {/* YOUR QUEUE */}
        <section>
          <div className="omh-section-head">
            <div className="omh-section-title">Your Queue</div>
            <span className={`omh-section-tag ${pendingReview.length > 0 ? "alert" : ""}`} data-testid="requires-you-tag">
              {pendingReview.length > 0 ? "REQUIRES YOU" : "ALL CLEAR"}
            </span>
          </div>

          {pendingReview.length > 0 && (
            <div className="omh-attention" data-testid="attention-card">
              <div className="omh-attention-icon"><WarningOctagon size={20} weight="bold" /></div>
              <div>
                <div className="omh-attention-title">{pendingReview.length} PO{pendingReview.length > 1 ? "s" : ""} NEED YOUR ATTENTION</div>
                <div className="omh-attention-sub">{pendingReview.length} extracted PO{pendingReview.length > 1 ? "s" : ""} awaiting confirmation</div>
              </div>
              <button onClick={() => nav("/orders?status=pending_review")} className="jde-btn jde-btn-primary" style={{ marginLeft: "auto" }} data-testid="goto-pending">
                Review <ArrowRight size={12} weight="bold" />
              </button>
            </div>
          )}

          <div className="omh-section-head" style={{ marginTop: 18 }}>
            <div className="omh-section-title">
              <ChartBar size={16} weight="bold" style={{ display: "inline", marginRight: 6, marginBottom: -2 }} />
              Operations Queue <span style={{ color: "var(--omh-text-muted)", fontWeight: 500, marginLeft: 6 }}>{openOrders.length}</span>
            </div>
            <span className="omh-section-tag">ITEMS IN PIPELINE</span>
          </div>

          <div className="omh-action-grid">
            <ActionTile
              icon={FileText}
              value={pendingReview.length}
              title="Pending review"
              sub="AI-extracted, awaiting your confirmation"
              onClick={() => nav("/orders?status=pending_review")}
              variant={pendingReview.length > 0 ? "alert" : ""}
              testid="action-pending"
            />
            <ActionTile
              icon={Truck}
              value={partial.length}
              title="Partially shipped"
              sub="Open quantity remaining vs invoiced"
              onClick={() => nav("/orders?status=partially_shipped")}
              variant={partial.length > 0 ? "warning" : ""}
              testid="action-partial"
            />
            <ActionTile
              icon={CheckCircle}
              value={fullyShipped.length}
              title="Fully shipped"
              sub="Completed orders, ready to close"
              onClick={() => nav("/orders?status=fully_shipped")}
              variant={fullyShipped.length > 0 ? "success" : ""}
              testid="action-shipped"
            />
          </div>
        </section>

        {/* WATCHLISTS */}
        <section>
          <div className="omh-section-head">
            <div className="omh-section-title">★ Watchlists</div>
            <span className="omh-section-tag" data-testid="watchlist-tag">6 ACTIVE</span>
          </div>

          <div className="omh-watchlist" data-testid="watchlist">
            <WatchRow
              icon={WarningOctagon}
              variant="alert"
              title="Ingestion failures"
              sub="emails AI couldn't extract · FAIL01"
              count={stats?.extraction_failures ?? 0}
              countVariant={(stats?.extraction_failures ?? 0) > 0 ? "alert" : "zero"}
              to="/failures"
              testid="watch-failures"
            />
            <WatchRow
              icon={WarningOctagon}
              variant="alert"
              title="Quantity discrepancies"
              sub={`shipped > ordered · DSC1100`}
              count={discrep}
              countVariant={discrep > 0 ? "alert" : "zero"}
              to="/discrepancies"
              testid="watch-discrep"
            />
            <WatchRow
              icon={Clock}
              variant="warning"
              title="Aging > 30 days"
              sub="open orders awaiting shipment · AGE3010"
              count={aging30}
              countVariant={aging30 > 0 ? "alert" : "zero"}
              to="/aging"
              testid="watch-aging"
            />
            <WatchRow
              icon={Sparkle}
              variant="info"
              title="AI confidence < 80%"
              sub="extractions to spot-check · AI4420"
              count={pos.filter((p) => p.confidence != null && p.confidence < 0.8).length}
              countVariant="zero"
              to="/orders?status=pending_review"
              testid="watch-aiconf"
            />
            <WatchRow
              icon={Receipt}
              variant="info"
              title="Invoices captured (30d)"
              sub="awaiting reconciliation · INV5500"
              count={invoices.length}
              countVariant="zero"
              to="/invoices"
              testid="watch-invoices"
            />
            <WatchRow
              icon={UsersIcon}
              variant="success"
              title="Active customers"
              sub="with open orders · CUS7700"
              count={customers.filter((c) => c.total_open_orders > 0).length}
              countVariant="zero"
              to="/customers"
              testid="watch-customers"
            />
          </div>
        </section>
      </div>

      {/* ===== OPEN ORDERS TABLE ===== */}
      <div className="jde-panel" style={{ marginTop: 26 }}>
        <div className="jde-panel-header">
          <span><ListChecks size={14} weight="bold" style={{ display: "inline", marginRight: 6, marginBottom: -2 }} /> Open Orders Overview</span>
          <Link to="/orders" className="jde-btn jde-btn-sm" data-testid="goto-orders">
            View All <ArrowRight size={11} weight="bold" />
          </Link>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="jde-grid" data-testid="dashboard-orders-grid">
            <thead>
              <tr>
                <th>PO #</th>
                <th>Customer</th>
                <th>Status</th>
                <th className="num">Lines</th>
                <th className="num">Ordered</th>
                <th className="num">Shipped</th>
                <th className="num">Open</th>
                <th className="num">Aging (d)</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {openOrders.slice(0, 10).map((p) => (
                <tr key={p.id} data-testid={`dash-row-${p.id}`} onClick={() => nav(`/po/${p.id}`)}>
                  <td className="text-mono" style={{ color: "var(--omh-primary)", fontWeight: 700 }}>{p.po_number || "—"}</td>
                  <td>{p.customer_name || <span className="muted">—</span>}</td>
                  <td><StatusBadge status={p.status} /></td>
                  <td className="num">{p.line_items?.length || 0}</td>
                  <td className="num">{fmt(p.total_ordered_qty)}</td>
                  <td className="num">{fmt(p.total_shipped_qty)}</td>
                  <td className="num" style={{ fontWeight: 700 }}>{fmt(p.total_open_qty)}</td>
                  <td className="num" style={{ color: p.aging_days > 30 ? "var(--omh-alert)" : undefined, fontWeight: p.aging_days > 30 ? 700 : undefined }}>{p.aging_days}</td>
                  <td className="muted" style={{ fontSize: 11 }}>{(p.source_type || "").replace("_", " ")}</td>
                </tr>
              ))}
              {openOrders.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: "center", padding: 24 }} className="muted">
                  No open orders yet. Upload a PO or poll Gmail to get started.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="jde-status-bar">
          <span>Total open orders: {openOrders.length}</span>
          <span>Showing top 10 by date</span>
        </div>
      </div>
    </Layout>
  );
}

function MiniTile({ label, value, variant, onClick, testid }) {
  return (
    <button type="button" className={`omh-mini-tile ${variant || ""}`} onClick={onClick} data-testid={testid}>
      <div className="omh-mini-tile-label">{label}</div>
      <div className="omh-mini-tile-value">{value}</div>
    </button>
  );
}

function Tile({ label, value, icon: Icon, variant, sub, testid }) {
  return (
    <div className={`jde-tile ${variant}`} data-testid={testid}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <span className="tile-label">{label}</span>
        <Icon size={18} weight="bold" style={{ opacity: 0.45 }} />
      </div>
      <div className="tile-value">{value}</div>
      {sub && <div className="tile-sub">{sub}</div>}
    </div>
  );
}

function ActionTile({ icon: Icon, value, title, sub, onClick, variant, testid }) {
  return (
    <div className={`omh-action-tile ${variant || ""}`} onClick={onClick} data-testid={testid}>
      <div className="omh-action-tile-icon"><Icon size={16} weight="bold" /></div>
      <div className="omh-action-tile-value">{value}</div>
      <div>
        <div className="omh-action-tile-title">{title}</div>
        <div className="omh-action-tile-sub">{sub}</div>
      </div>
    </div>
  );
}

function WatchRow({ icon: Icon, variant, title, sub, count, countVariant, to, testid }) {
  return (
    <Link to={to} className="omh-watch-row" data-testid={testid}>
      <div className={`omh-watch-icon ${variant || ""}`}><Icon size={16} weight="bold" /></div>
      <div className="omh-watch-body">
        <div className="omh-watch-title">{title}</div>
        <div className="omh-watch-sub">{sub}</div>
      </div>
      <div className={`omh-watch-count ${countVariant || ""}`}>{count}</div>
      <CaretRight size={14} weight="bold" className="omh-watch-caret" />
    </Link>
  );
}

export function StatusBadge({ status }) {
  const map = {
    open: { cls: "open", label: "Open" },
    pending_review: { cls: "pending", label: "Pending" },
    confirmed: { cls: "confirmed", label: "Confirmed" },
    partially_shipped: { cls: "partial", label: "Partial" },
    fully_shipped: { cls: "shipped", label: "Shipped" },
    closed: { cls: "shipped", label: "Closed" },
    hold: { cls: "hold", label: "Hold" },
    cancelled: { cls: "cancelled", label: "Cancelled" },
    pushed_to_erp: { cls: "confirmed", label: "Pushed to ERP" },
    superseded: { cls: "closed", label: "Superseded" },
  };
  const m = map[status] || { cls: "closed", label: status ? capitalize(status) : "—" };
  return <span className={`jde-badge ${m.cls}`}>{m.label}</span>;
}

function capitalize(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString();
}
